/**
 * The chips' live upload progress, kept outside React state: an upload fires
 * a progress event per chunk, and each one should repaint that file's ring —
 * not the panel, the player and the whole chat above it.
 */
import type { UploadProgressSource } from '../../../ChatThread';

export interface UploadProgressStore extends UploadProgressSource {
  /** Moves a file's ring; listeners hear only a value that changed. */
  set(id: string, progress: number): void;
  /** Drops a file's live value, so its chip falls back to the file's own `progress`. */
  forget(id: string): void;
}

export function createUploadProgressStore(): UploadProgressStore {
  const progressById = new Map<string, number>();
  const listeners = new Set<() => void>();

  function notify(): void {
    listeners.forEach((listener) => listener());
  }

  return {
    get(id) {
      return progressById.get(id);
    },
    subscribe(onChange) {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
    set(id, progress) {
      if (progressById.get(id) === progress) return;
      progressById.set(id, progress);
      notify();
    },
    forget(id) {
      if (!progressById.delete(id)) return;
      notify();
    },
  };
}
